import { Suspense, lazy } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Trans } from "react-i18next";

import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";
import { handleInterfaceLanguageChange } from "../../utils/user-utils";

import { ErrorMessageBanner } from "components/forms/ErrorMessageBanner";
import { ResetPasswordModal } from "components/header/ResetPasswordModal";
import { INTERFACE_LANGUAGES, LANGUAGE_LABELS, i18n, t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { LegacyButton } from "mds/components/LegacyButton";
import { LinkButton } from "mds/components/LinkButton";
import { showConfirmationModal, showModalWithProviders } from "mds/components/Modal";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentUserId, userSelectors } from "store/selectors";
import { useAv } from "utils/av";
import { Link } from "utils/routing";

interface AccountSettingsFormValue {
  first_name: string;
  last_name: string;
}

interface AccountSettingsModalProps {
  onClose: () => void;
}

const showResetPasswordModal = () => {
  return showConfirmationModal({
    title: t("profile_panel.reset_password"),
    children: (onClose) => <ResetPasswordModal onClose={onClose} />,
    hideCancelConfirmButtons: true,
  });
};

const SystemCheck = lazy(() => import("components/av/SystemCheck"));

const showSystemCheckModal = () => {
  showModalWithProviders((unmount) => (
    <Suspense fallback={t("common.loading")}>
      <SystemCheck onClose={unmount} />
    </Suspense>
  ));
};

export const AccountSettingsModal = ({ onClose }: AccountSettingsModalProps) => {
  const userId = useAppSelector(selectCurrentUserId);
  const user = useAppSelector((s) => userSelectors.selectById(s, userId));
  const form = useForm<AccountSettingsFormValue>({
    defaultValues: { first_name: user?.first_name, last_name: user?.last_name },
  });
  const errors = form.formState.errors;
  const { isInVirtualClass } = useAv();

  if (!userId) {
    return null;
  }

  const onSubmit: SubmitHandler<AccountSettingsFormValue> = (formData) => {
    storeApi.users.partial_update({ id: userId, ...formData }).then(() => {
      toastSuccessMessage(t("success.toasts.updated_account_settings"));
    });
    onClose();
  };

  return (
    <FormProvider {...form}>
      <form
        className="pt-5 text-sm font-medium leading-[1.3125rem] text-black-tint-20"
        onSubmit={form.handleSubmit((data) => {
          trackEvent("Account Settings - Save changes", BUTTON_EVENT_CATEGORY);
          onSubmit(data);
        })}
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <div>{t("user_info.email")}</div>
            <input
              className="w-full rounded-md border border-black-tint-90 bg-black-tint-97 p-2 font-medium text-black-tint-55"
              id="email"
              type="text"
              value={user?.email}
              disabled
            />
          </label>

          <div>
            <label className="flex flex-col gap-1">
              <div>{t("user_info.first_name")}</div>
              <input
                aria-invalid={errors.first_name ? "true" : "false"}
                className="w-full rounded-md border border-black-tint-90 p-2 font-medium aria-invalid:border-red"
                {...form.register("first_name", {
                  required: t("error.field_required"),
                  validate: {
                    notBlank: (value: string) => {
                      if (value.trim() === "") {
                        return t("error.field_not_blank");
                      }
                    },
                  },
                })}
                id="first_name"
                type="text"
              />
            </label>
            <ErrorMessageBanner name="first_name" />
          </div>

          <div>
            <label className="flex flex-col gap-1">
              <div>{t("user_info.last_name")}</div>
              <input
                aria-invalid={errors.last_name ? "true" : "false"}
                className="w-full rounded-md border border-black-tint-90 p-2 font-medium aria-invalid:border-red"
                {...form.register("last_name")}
                id="last_name"
                type="text"
              />
            </label>
            <ErrorMessageBanner name="last_name" />
          </div>
        </div>

        <div className="flex items-center justify-between pt-4">
          <div className="text-black-tint-10">{t("profile_panel.select_language")}</div>
          <DropdownMenu
            items={INTERFACE_LANGUAGES.map((lang) => ({
              id: lang,
              title: LANGUAGE_LABELS[lang],
            }))}
            selectedValue={i18n.language}
            onSelectionChange={({ id }) => handleInterfaceLanguageChange(id)}
          />
        </div>

        <div className="pt-4">
          <LinkButton
            eventName="Take System Check"
            onClick={() => {
              onClose();
              if (isInVirtualClass) {
                window.open("/system-check", "_blank");
                return;
              }
              showSystemCheckModal();
            }}
          >
            {t("profile_panel.take_system_check")}
          </LinkButton>
        </div>

        <div className="pt-4">
          <LinkButton
            eventName="Reset Password"
            onClick={() => {
              onClose();
              showResetPasswordModal();
            }}
          >
            {t("profile_panel.reset_password")}
          </LinkButton>
        </div>

        <div className="body-xs">
          <Trans i18nKey="user_info.eula_footer">
            <Link target="_blank" to="/terms-of-service-en">
              Terms of Service
            </Link>
            <Link target="_blank" to="/privacy-policy-en">
              Privacy Policy
            </Link>
            <Link target="_blank" to="/third-party-licenses">
              Third-party Licenses
            </Link>
          </Trans>
        </div>

        <div className="flex justify-end gap-2 pt-8">
          <LegacyButton kind="secondary" size="xs" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </LegacyButton>

          <LegacyButton kind="primary" size="xs" title={t("common.save_changes")} type="submit">
            {t("common.save_changes")}
          </LegacyButton>
        </div>
      </form>
    </FormProvider>
  );
};
